// SL.L1a-2 (#2957) — the board's sprint writes (routes/sprints.ts): approve,
// return to draft, start and close, and the close-with-override with its
// close-gate read (SL.G1 #2953).
import { ApiError, apiFetch } from './api';
import type { SprintItemDisposition } from '@blueprint/shared';

const enc = encodeURIComponent;
const sprintPath = (projectId: string, sprintId: string) => `/projects/${enc(projectId)}/sprints/${enc(sprintId)}`;

/** The POSTs that take no body (`emptyBody`). */
export type SprintTransitionPath = 'approve' | 'return-to-draft' | 'start' | 'close';

export function postSprintTransition(projectId: string, sprintId: string, path: SprintTransitionPath): Promise<unknown> {
  return apiFetch(`${sprintPath(projectId, sprintId)}/${path}`, { method: 'POST', body: '{}' });
}

export interface CloseGateOpenItem {
  itemId: string;
  key: string;
  type: string;
  issueIds: string[];
  issueStatuses: string[];
  /** A preselection only: the override still sends one disposition per open item. */
  defaultDisposition: SprintItemDisposition;
  why: string;
}

export type CloseGateCarry =
  | { available: true; sprintId: string; number: number }
  | { available: false; refusal: string };

export interface CloseGatePreview {
  sprintId: string;
  number: number;
  result: 'pass' | 'fail';
  openItems: CloseGateOpenItem[];
  carry: CloseGateCarry;
}

export function fetchCloseGate(projectId: string, sprintId: string): Promise<CloseGatePreview> {
  return apiFetch<CloseGatePreview>(`${sprintPath(projectId, sprintId)}/close-gate`);
}

export interface OverrideDisposition { itemId: string; disposition: SprintItemDisposition; reason?: string }

export function closeSprintWithOverride(
  projectId: string, sprintId: string, body: { reason: string; dispositions: OverrideDisposition[] },
): Promise<unknown> {
  return apiFetch(`${sprintPath(projectId, sprintId)}/close-with-override`, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * The server's words for a refusal (#3055): a Zod 400's `details[].message`
 * (its `error` is only "Validation failed"), else `error`; `fallback` when no
 * server answered.
 */
export function refusalText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  // middleware/validate.ts: every detail carries a message.
  const details = err.body.details;
  if (Array.isArray(details) && details.length > 0) return details.map((d: { message: string }) => d.message).join(' ');
  return err.message;
}

/** SL.G1's SPIKE refusal (ruling 4): matched on `code` only, never on the text. */
export interface SpikeRefusal { backlogItemId: string; key: string }

export function spikeRefusal(err: unknown): SpikeRefusal | null {
  const body = (err as Partial<ApiError> | null)?.body;
  if (body?.code !== 'SPIKE_OUTCOME_REQUIRED') return null;
  return { backlogItemId: String(body.backlogItemId), key: String(body.key) };
}
